import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from 'lucide-react';
import { Timestamp, doc, getDoc, deleteField } from 'firebase/firestore';
import { Confirmation as FirestoreConfirmation } from '@/types/firestore';
import { db } from '@/lib/firebase';
import { useAppSelector } from '@/store/hooks';
import { selectSelectedEntity, selectEditorOperationLoading } from '@/store/slices/editorSlice';

// Interface for the actual data structure in Firestore if different from EditableConfirmation
// For now, assuming FirestoreConfirmation from types/firestore is the source of truth

// Extended type for editing that includes temporary fields like timestampString
interface EditableConfirmationInternal extends FirestoreConfirmation {
  timestampString?: string;
  // sightingId is not part of the Firestore document for a confirmation itself,
  // but it's needed for context to fetch/save. It will be passed as a prop.
}

interface ConfirmationEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // confirmation: any | null; // Removed confirmation prop
  sightingId: string; // Crucial for fetching the correct confirmation
  onSave: (confirmation: Partial<FirestoreConfirmation>) => Promise<void>; // Save expects FirestoreConfirmation
  // isLoading: boolean; // Removed isLoading prop
}

export const ConfirmationEditDialog: React.FC<ConfirmationEditDialogProps> = ({
  open,
  onOpenChange,
  sightingId, // Keep sightingId prop
  onSave,
}) => {
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const isSaveLoading = useAppSelector(selectEditorOperationLoading) === 'pending';

  const [editingConfirmation, setEditingConfirmation] = useState<Partial<EditableConfirmationInternal> | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  // Store the last fetched ID to prevent re-fetching same data
  const [lastFetchedConfirmationId, setLastFetchedConfirmationId] = useState<string | null>(null);
  const [lastFetchedSightingId, setLastFetchedSightingId] = useState<string | null>(null);

  useEffect(() => {
    const confirmationId = selectedEntity?.id;

    if (open && selectedEntity?.type === 'confirmation' && confirmationId && sightingId) {
      // Only fetch if IDs have changed or no data is loaded yet
      if (confirmationId !== lastFetchedConfirmationId || sightingId !== lastFetchedSightingId || !editingConfirmation) {
        setIsFetchingDetails(true);
        setFetchError(null);
        // Do NOT setEditingConfirmation(null) here if IDs are same, to prevent flicker
        // It will be set by the fetch result or if IDs truly change.
        if (confirmationId !== lastFetchedConfirmationId || sightingId !== lastFetchedSightingId) {
            setEditingConfirmation(null); // Clear if IDs actually changed
        }

        getDoc(doc(db, 'sightings', sightingId, 'confirmations', confirmationId))
          .then(docSnap => {
            if (docSnap.exists()) {
              const fetchedData = { ...docSnap.data(), id: docSnap.id } as FirestoreConfirmation;
              const formatted: Partial<EditableConfirmationInternal> = { ...fetchedData };
              if (fetchedData.timestamp instanceof Timestamp) {
                formatted.timestampString = fetchedData.timestamp.toDate().toISOString().split('T')[0];
              }
              setEditingConfirmation(formatted);
              setLastFetchedConfirmationId(confirmationId);
              setLastFetchedSightingId(sightingId);
            } else {
              setFetchError("Confirmation not found.");
              setEditingConfirmation(null); // Clear if not found
              console.error("Error: Confirmation document not found for ID:", confirmationId, "in Sighting:", sightingId);
            }
          })
          .catch(error => {
            console.error("Error fetching confirmation details:", error);
            setFetchError("Failed to load confirmation details.");
            setEditingConfirmation(null); // Clear on error
          })
          .finally(() => {
            setIsFetchingDetails(false);
          });
      } else {
        // Data already loaded for these IDs, and not currently fetching
        setIsFetchingDetails(false); 
      }
    } else if (!open) {
      // Clear state when dialog is closed
      setEditingConfirmation(null);
      setFetchError(null);
      setIsFetchingDetails(false);
      setLastFetchedConfirmationId(null);
      setLastFetchedSightingId(null);
    }
  }, [open, selectedEntity, sightingId, lastFetchedConfirmationId, lastFetchedSightingId, editingConfirmation]);

  // Handle keyboard escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) onOpenChange(false);
    };
    if (open) window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [open, onOpenChange]);

  // Handle field changes - only timestampString is directly editable here
  const handleTimestampChange = (dateString: string) => {
    setEditingConfirmation((prev) => {
      if (!prev) return null;
      // Allow clearing the date, but ensure timestamp becomes null then, not an invalid Date
      const newTimestamp = dateString ? Timestamp.fromDate(new Date(dateString)) : null;
      return { ...prev, timestampString: dateString, timestamp: newTimestamp as Timestamp | undefined };
    });
  };

  const handleOpenChangeInternal = (newOpenState: boolean) => {
    if (!newOpenState) {
      // Reset last fetched IDs on explicit close to ensure fresh fetch next time if needed
      setLastFetchedConfirmationId(null); 
      setLastFetchedSightingId(null);
    }
    onOpenChange(newOpenState);
  };

  const handleSave = async () => {
    if (!editingConfirmation || isSaveLoading) return; 
    
    const { timestampString, id, timestamp, ...otherDataToSave } = editingConfirmation;
    const finalData: Partial<FirestoreConfirmation> = { ...otherDataToSave };

    if (timestamp === null) { // Intentionally set to null by clearing date picker
      finalData.timestamp = deleteField() as any;
    } else if (timestamp instanceof Timestamp) { // Is a valid timestamp object
      finalData.timestamp = timestamp;
    }
    // If timestamp was undefined in editingConfirmation, it means it wasn't touched or loaded,
    // so it won't be included in finalData, and Firestore won't update/delete it.

    try {
      await onSave(finalData);
    } catch (error) {
      console.error('Error saving confirmation:', error);
      // Error toast is likely handled by the thunk/onSave caller
    }
  };

  const handleCancel = () => handleOpenChangeInternal(false);

  const isLoading = isFetchingDetails;
  const displayError = fetchError;

  if (!open) return null;

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px] flex items-center justify-center">
          <DialogHeader>
            <DialogTitle>Loading Data</DialogTitle>
            <DialogDescription>
              Please wait while the confirmation details are being loaded.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" /> Loading...
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (displayError) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>{displayError}</DialogDescription>
          </DialogHeader>
          <DialogFooter><Button variant="outline" onClick={handleCancel}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Added a check for selectedEntity.type to ensure we only render this specific error if it *should* be a confirmation dialog
  if (selectedEntity?.type === 'confirmation' && !editingConfirmation && !isLoading && !displayError) {
     return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
           <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>Could not load confirmation data. Ensure it exists for Sighting ID: {sightingId} and Confirmation ID: {selectedEntity?.id}.</DialogDescription>
            </DialogHeader>
           <DialogFooter><Button variant="outline" onClick={handleCancel}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!editingConfirmation) return null; // General fallback if no data

  return (
    <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Confirmation</DialogTitle>
          <DialogDescription>
            Make changes to the confirmation timestamp below.
            {editingConfirmation.username && <span className="block mt-1">For user: {editingConfirmation.username}</span>}
            {isLoading && <span className="text-xs text-muted-foreground ml-2">(Updating...)</span>} 
          </DialogDescription>
        </DialogHeader>
        
        <div className={`grid gap-4 py-4 ${isSaveLoading || isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="timestamp" className="text-right">Date</Label>
            <Input
              id="timestamp"
              type="date"
              value={editingConfirmation.timestampString || ''} // Ensure it handles undefined timestampString gracefully
              onChange={(e) => handleTimestampChange(e.target.value)}
              className="col-span-3"
              disabled={isSaveLoading || isLoading}
            />
          </div>
        </div>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={handleCancel} disabled={isSaveLoading || isLoading}>Cancel</Button>
          </DialogClose>
          {/* Button is disabled if save is in progress, or if fetching details, or if timestampString is empty (meaning no valid date selected) 
              Removed !editingConfirmation.timestampString check to allow clearing the date */}
          <Button onClick={handleSave} disabled={isSaveLoading || isLoading}>
            {isSaveLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
            ) : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 